import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, CardElement, useStripe, useElements} from '@stripe/react-stripe-js';

import { detect } from '../../../utils/detect-browser';
import CssTransition from '../../Transition/CssTransition';
import { OnSubmitValidator } from '../../../containers/DefaultStyles/FormDetails';
import {
    AnimatiedFormContainer,
    BackLink,
    BottomGroup,
    ButtonGroup,
    Container,
    DetailsFormContainer,
    Disclaimer,
    DisclaimerContainer,
    FormGroupWrapper,
    FormSectionContainer,
    Info,
    PaymentButtonsContainer,
    SmallFormGroup,
    LegacyCopyText
} from './FormComponents';
import CreditCardInputs from './CreditCardInputs';
import { PaymentTypeButton, NativePaymentsIcon } from './PaymentButtons';
import Button from '../../Button';
import TwoGroup from '../../TwoGroup';
import InputGroup from '../../InputGroup';

import GetUpdatesCheckbox from '../../Widget/GetUpdatesCheckbox';
import LegalInfo from '../../Widget/LegalInfo';

import { widgetBounce } from '../../../containers/Widget/actions';
import {
    onBackToAmount,
    setBraintreeDeviceInfo,
    submitDonationCompleted,
    selectPaymentProvider,
    toggleCreditCardForm,
    onOrgHunterCcValidityChange
} from '../../../containers/Donation/actions';

import MobileDetailsHeader from '../MobileDetailsHeader';
import TransitionGroupContainer from '../../Transition/TransitionGroupContainer';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../../utils/nextActionUtil';
import { CardTypes, NativePaymentsTypes, PaymentMethod } from '../../../utils/enums';
import {
    spawnPaymentTab,
    paymentPageCleanup
} from '../../../utils/donation/alternateTabPayment';

import submissionErrorHandler from '../../../utils/submissionErrorHandler';
import { getCachedFullName } from '../../../utils/formCaching/helpers';

import { copyFor, capitalization } from '../../../copy/copy';
import validate from './validations';
import config from '../../../config';
import { isPortrait } from '../../../utils/size';

import getIsLegacyDonation from '../../../utils/donation/isLegacyDonation';

class PaymentForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validate, props),
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
        this.checkForBlurError = this.checkForBlurError.bind(this);
        this.setIsStripeDataValid = this.setIsStripeDataValid.bind(this);
    }

    setIsStripeDataValid(value){
        this.setState({isStripeDataValid: value});
    }

    static propTypes = {
        actionIdentifier: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
        amountFormated: PropTypes.string.isRequired,
        apiRequestData: PropTypes.object.isRequired,
        awaitingBraintreeResponse: PropTypes.bool.isRequired,
        deviceData: PropTypes.string,
        currentFormValues: PropTypes.object,
        endUserLocation: PropTypes.object,
        feedbackText: PropTypes.string,
        formValues: PropTypes.object,
        getUpdatesText: PropTypes.string,
        hasChoiceOfPaymentProvider: PropTypes.bool.isRequired,
        invalid: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        isMultiActionsCard: PropTypes.bool.isRequired,
        isMobileResolution: PropTypes.bool.isRequired,
        onBackToAmount: PropTypes.func.isRequired,
        onComplete: PropTypes.func.isRequired,
        onPartnerClick: PropTypes.func.isRequired,
        onSubmit: PropTypes.func.isRequired,
        partnerLogo: PropTypes.string,
        partnerName: PropTypes.string,
        paymentInfo: PropTypes.object.isRequired,
        paymentMethod: PropTypes.string.isRequired,
        selectedAmountTime: PropTypes.instanceOf(Date),
        selectedPaymentProvider: PropTypes.number,
        selectPaymentProvider: PropTypes.func.isRequired,
        setBraintreeDeviceInfo: PropTypes.func.isRequired,
        showCreditCardForm: PropTypes.bool.isRequired,
        skipEndCard: PropTypes.bool.isRequired,
        submitDonationCompleted: PropTypes.func.isRequired,
        submitting: PropTypes.bool.isRequired,
        supportedNativePaymentTypes: PropTypes.arrayOf(PropTypes.number.isRequired)
            .isRequired,
        toggleCreditCardForm: PropTypes.func.isRequired,
        valid: PropTypes.bool.isRequired,
        orgHunterCcValid: PropTypes.bool,
        onOrgHunterCcValidityChange: PropTypes.func.isRequired,
        widgetBounce: PropTypes.func.isRequired,
        bounceDismissed: PropTypes.bool.isRequired,
        widgetRequestId: PropTypes.string.isRequired,
        articleUrl: PropTypes.string.isRequired
    };

    handleExternalPayment(typeName) {
        this.props.setBraintreeDeviceInfo(this.props.paymentInfo);
        spawnPaymentTab(
            this.props.paymentMethod,
            typeName,
            this.props.paymentInfo.trackingId,
            this.props.paymentInfo.clientToken,
            this.props.apiRequestData,
            this.props.amount,
            copyFor('general', 'applePayDonationMessage', 'donation', null, {
                partnerName: this.props.partnerName
            }),
            this.props.partnerName
        ).then(data => {
            // the other tab should have set the payment nonce on the server, so we can complete the payment now
            this.props.submitDonationCompleted(
                this.props.apiRequestData,
                {
                    trackingId: this.props.paymentInfo.trackingId,
                    ...this.props.formValues
                },
                this.props.isMultiActionsCard,
                this.props.actionIdentifier,
                this.props.selectedAmountTime,
                this.props.feedbackText,
                this.props.partnerName,
                this.props.skipEndCard,
                data.paymentProvider,
                data.providerSpecificInfo,
                this.props.deviceData
            );
            this.props.onComplete();
        });
    }

    componentWillUnmount() {
        paymentPageCleanup();
    }

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validate, this.props);
        if ((errors.includes(name) && !this.state.errors.includes(name)) ||
            (name === 'cardNumber' && this.state.isStripeDataValid === false))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }
    render() {
        const { isShortRow } = this.props;

        return (
            <DetailsFormContainer autoComplete="on">
                {this.props.isMobileResolution && (
                    <MobileDetailsHeader
                        partnerLogo={this.props.partnerLogo}
                        onPartnerClick={this.props.onPartnerClick}
                    />
                )}
                <TransitionGroup component={TransitionGroupContainer}>
                    <Container>
                        <FormSectionContainer onSubmit={this.props.onSubmit}>
                            {this.props.isMobileResolution && (
                                <Info>
                                    {copyFor(
                                        'general',
                                        'enterPaymentInfo',
                                        'donation',
                                        capitalization.first
                                    )}
                                </Info>
                            )}
                            <div>
                                <Field
                                    autoFocus
                                    className={'fs-block'}
                                    isShortRow={isShortRow}
                                    name="fullName"
                                    onBlur={() => this.checkForBlurError('fullName')}
                                    submissionError={this.state.errors.includes('fullName')}
                                    clearSubmitErrors={this.clearSubmitErrors}
                                    component={InputGroup}
                                    type="text"
                                    label="Full Name"
                                    placeholder="Full Name"
                                    autocomplete="cc-name"
                                    maxLength={100}
                                />
                                <Field
                                    className={'fs-block'}
                                    isShortRow={isShortRow}
                                    name="email"
                                    onBlur={() => this.checkForBlurError('email')}
                                    submissionError={this.state.errors.includes('email')}
                                    clearSubmitErrors={this.clearSubmitErrors}
                                    component={InputGroup}
                                    type="email"
                                    label="Email address"
                                    autocomplete="email"
                                    placeholder="Email Address"
                                    maxLength={100}
                                />
                            </div>
                            {this.props.hasChoiceOfPaymentProvider &&
                                !this.props.showCreditCardForm && (
                                    <Info visible={!this.props.valid}>
                                        {copyFor(
                                            'general',
                                            'selectPaymentMethod',
                                            'donation',
                                            capitalization.first
                                        )}
                                    </Info>
                                )}
                            {(!this.props.hasChoiceOfPaymentProvider ||
                                this.props.showCreditCardForm) && (
                                <TransitionGroup>
                                    <CssTransition
                                        key="cc-form"
                                        in={this.props.showCreditCardForm}
                                    >
                                        <AnimatiedFormContainer>
                                            {this.props.isMobileResolution && (
                                                <BackLink
                                                    hidden={
                                                        !this.props
                                                            .hasChoiceOfPaymentProvider
                                                    }
                                                    onClick={() =>
                                                        this.props.toggleCreditCardForm(
                                                            this.props.showCreditCardForm
                                                        )
                                                    }
                                                >
                                                    {copyFor(
                                                        'buttons',
                                                        'backToPaymentType',
                                                        'donation',
                                                        capitalization.first
                                                    )}
                                                </BackLink>
                                            )}
                                            <CreditCardInputs
                                                setIsStripeDataValid={this.setIsStripeDataValid}
                                                isStripeDataValid={this.state.isStripeDataValid}
                                                isShortRow={isShortRow}
                                                errors={this.state.errors}
                                                checkForBlurError={this.checkForBlurError}
                                                clearSubmitErrors={this.clearSubmitErrors}
                                                paymentMethod={this.props.paymentMethod}
                                                onOrgHunterCcValidityChange={
                                                    this.props.onOrgHunterCcValidityChange
                                                }
                                                onClick={()=>{
                                                    const errors = submissionErrorHandler(this.props.currentFormValues, validate, this.props);
                                                    this.setSubmitErrors(errors.filter(e => e==='fullName' || e==='email'));
                                                }}
                                            />
                                            <Field
                                                className={'fs-block'}
                                                isShortRow={isShortRow}
                                                name="dedication"
                                                component={InputGroup}
                                                type="text"
                                                label="Dedication"
                                                placeholder="dedication"
                                                autocomplete="none"
                                                maxLength={100}
                                                isOptional
                                            />
                                            {this.props.isLegacyDonation &&
                                                <LegacyCopyText isPortrait={isPortrait()} />}
                                            <SmallFormGroup
                                                style={{ marginTop: '-0.5em' }}
                                            >
                                                <FormGroupWrapper>
                                                    <GetUpdatesCheckbox
                                                        isEU={
                                                            this.props.endUserLocation
                                                                .isEU
                                                        }
                                                        labelText={
                                                            this.props.getUpdatesText
                                                        }
                                                    />
                                                    <LegalInfo align="right" />
                                                </FormGroupWrapper>
                                            </SmallFormGroup>
                                        </AnimatiedFormContainer>
                                    </CssTransition>
                                </TransitionGroup>
                            )}
                            <PaymentButtonsContainer
                                isDesktop={!this.props.isMobileResolution}
                                id="NativePaymentButtonWrapper"
                            >
                                {!this.props.showCreditCardForm &&
                                    this.props.supportedNativePaymentTypes.map(
                                        paymentType => (
                                            <PaymentTypeButton
                                                isDesktop={!this.props.isMobileResolution}
                                                numberOfPaymentTypes={
                                                    this.props.supportedNativePaymentTypes
                                                        .length
                                                }
                                                hidden={
                                                    (!this.props
                                                        .hasChoiceOfPaymentProvider &&
                                                        !this.props.showCreditCardForm) ||
                                                    (!this.props.isMobile &&
                                                        paymentType ===
                                                            NativePaymentsTypes.venmo)
                                                }
                                                valid={
                                                    this.props.valid &&
                                                    !this.props.awaitingBraintreeResponse
                                                }
                                                onClick={e => {
                                                    e.preventDefault();
                                                    if (!this.props.valid) {
                                                        const errors = submissionErrorHandler(this.props.currentFormValues, validate, this.props);
                                                        this.setSubmitErrors(errors.filter(e => e==='fullName' || e==='email'));
                                                        return;
                                                    }
                                                    this.props.selectPaymentProvider(
                                                        paymentType,
                                                        this.props.selectedPaymentProvider
                                                    );

                                                    switch (paymentType) {
                                                        case NativePaymentsTypes.applePay:
                                                            this.handleExternalPayment(
                                                                'applePay'
                                                            );
                                                            break;
                                                        case NativePaymentsTypes.googlePay:
                                                            this.handleExternalPayment(
                                                                'googlePay'
                                                            );
                                                            break;
                                                        case NativePaymentsTypes.venmo:
                                                            this.handleExternalPayment(
                                                                'venmo'
                                                            );
                                                            break;
                                                        default:
                                                            return;
                                                    }
                                                }}
                                                key={paymentType}
                                                className="ie-payment-button-container-parent"
                                            >
                                                <NativePaymentsIcon
                                                    isDesktop={
                                                        !this.props.isMobileResolution
                                                    }
                                                    paymentType={paymentType}
                                                />
                                            </PaymentTypeButton>
                                        )
                                    )}
                            </PaymentButtonsContainer>
                            {!this.props.hasChoiceOfPaymentProvider &&
                                this.props.showCreditCardForm && (
                                    <SmallFormGroup>
                                        <FormGroupWrapper>
                                            <GetUpdatesCheckbox
                                                isEU={this.props.endUserLocation.isEU}
                                                labelText={this.props.getUpdatesText}
                                            />
                                            <LegalInfo align="right" />
                                        </FormGroupWrapper>
                                    </SmallFormGroup>
                                )}
                            <BottomGroup
                                showCreditCardForm={this.props.showCreditCardForm}
                            >
                                <ButtonGroup>
                                    <TwoGroup>
                                        <Button
                                            label={copyFor(
                                                'buttons',
                                                'back',
                                                undefined,
                                                capitalization.eachWord
                                            )}
                                            onClick={() => {
                                                if (!this.props.bounceDismissed) {
                                                    this.props.widgetBounce(
                                                        true,
                                                        this.props.widgetRequestId,
                                                        this.props.articleUrl,
                                                        this.props.currentFormValues,
                                                        this.props.actionIdentifier
                                                    );
                                                } else {
                                                    this.props.onBackToAmount();
                                                }
                                            }}
                                        />
                                        <OnSubmitValidator
                                            onClick={e => {
                                                e.preventDefault();
                                                const errors = submissionErrorHandler(
                                                    this.props.currentFormValues,
                                                    validate,
                                                    {...this.props,
                                                        isStripeDataValid: this.state.isStripeDataValid}
                                                );
                                                if (errors.length === 0 && !this.props.invalid) {
                                                    if (
                                                        this.props.paymentMethod ===
                                                            PaymentMethod.OrgHunter &&
                                                        !this.props.orgHunterCcValid
                                                    )
                                                        return;
                                                    this.props.onSubmit(e);
                                                } else {
                                                    this.setSubmitErrors(errors);
                                                }
                                            }}
                                        >
                                            <Button
                                                style={
                                                    !this.props
                                                        .hasChoiceOfPaymentProvider ||
                                                    this.props.showCreditCardForm
                                                        ? {}
                                                        : { visibility: 'hidden' }
                                                }
                                                primary={true}
                                                label={copyFor(
                                                    'buttons',
                                                    'giveWithAmount',
                                                    'donation',
                                                    null,
                                                    {
                                                        amountFormated: `${
                                                            this.props.amountFormated
                                                        }`
                                                    }
                                                )}
                                                type="submit"
                                                disabled={
                                                    this.props.invalid ||
                                                    this.props.submitting ||
                                                    (this.props.paymentMethod ===
                                                        PaymentMethod.OrgHunter &&
                                                        !this.props.orgHunterCcValid)
                                                }
                                            />
                                        </OnSubmitValidator>
                                    </TwoGroup>
                                </ButtonGroup>
                            </BottomGroup>
                            {this.props.isMobileResolution &&
                             !this.props.isLegacyDonation && (
                                <DisclaimerContainer>
                                    <Disclaimer
                                        paymentMethod={this.props.paymentMethod}
                                    />
                                </DisclaimerContainer>
                            )}
                        </FormSectionContainer>
                    </Container>
                </TransitionGroup>
            </DetailsFormContainer>
        );
    }
}

const OrgHunterPaymentForm = (props) => {
    const stripe = useStripe();
    const elements = useElements();

    async function handleSubmit(e) {
        e.preventDefault();
        const cardElement = elements.getElement(CardElement);
        const data = await stripe.createToken(cardElement);
        props.onSubmit(e, true, data.token?.id);
    }

    return <PaymentForm {...props} onSubmit={handleSubmit} />;
};

OrgHunterPaymentForm.propTypes = {
    onSubmit: PropTypes.func.isRequired
};

class OrgHunterForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            stripe: null
        };
    }

    componentDidMount() {
        const stripePromise = loadStripe(config.ORG_HUNTER_PUBLIC_KEY);
        this.setState({ stripe: stripePromise });
    }

    render() {
        return !!this.state.stripe ? (
            <Elements
                stripe={this.state.stripe}
                fonts={[
                    {
                        family: 'Averta',
                        src: `url('https://s3.amazonaws.com/actionbutton.fonts/averta.woff') format('woff')`,
                        weight: 300,
                        style: 'normal'
                    }
                ]}
            >
                <OrgHunterPaymentForm {...this.props} stripe={this.state.stripe} />
            </Elements>
        ) : (<div />);
    }
}

class DonationForm extends React.Component {
    render() {
        return this.props.paymentMethod === PaymentMethod.OrgHunter ? (
            <OrgHunterForm {...this.props} />
        ) : (
            <PaymentForm {...this.props} />
        );
    }
}

DonationForm.propTypes = {
    paymentMethod: PropTypes.string.isRequired
};

export default compose(
    connect(
        (state,props) => {
            const nextAction = getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            );

            const isLegacyDonation = getIsLegacyDonation(state);

            return {
                initialValues: {
                    email: state.cachedFormData.email || '',
                    fullName: getCachedFullName(state) || '',
                    getUpdates: !(state.widget.endUserLocation || {}).isEU,
                    postalCode: state.cachedFormData.postalCode || ''
                },
                isMobile: state.widget.isMobile,
                endUserLocation: state.widget.endUserLocation || {},
                apiRequestData: state.widget.apiRequestData,
                actionIdentifier: state.widget.activeAction.identifier,
                amount: state.donation.selectedAmount,
                deviceData: state.donation.deviceData,
                paymentInfo: state.donation.paymentInfo,
                feedbackText: state.widget.activeAction.thankYouText || '',
                showCreditCardForm: state.donation.showCreditCardForm,
                isMultiActionsCard:
                    state.widget.cards[state.widget.activeAction.order - 1].type ===
                    CardTypes.multipleActions,
                formValues: (state.form.donationForm || {}).values,
                getUpdatesText: state.widget.activeAction.subscriptionOptInText,
                partnerName: state.widget.activeAction.partner.name,
                selectedAmountTime: state.donation.selectedAmountTime,
                selectedPaymentProvider: state.donation.selectedPaymentProvider,
                supportedNativePaymentTypes:
                    detect().name==="ie"
                    ? props.supportedNativePaymentTypes.filter(el=> el!==NativePaymentsTypes.googlePay)
                    : props.supportedNativePaymentTypes,
                skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction,
                awaitingBraintreeResponse: state.donation.awaitingBraintreeResponse,
                orgHunterCcValid:
                    state.donation.orgHunterCcValidity.cardNumber &&
                    state.donation.orgHunterCcValidity.expDate &&
                    state.donation.orgHunterCcValidity.cvv,
                currentFormValues: (state.form['donationForm'] || {}).values,
                widgetRequestId: state.widget.widgetRequestId,
                articleUrl: state.widget.articleUrl,
                bounceDismissed: state.widget.bounceDismissed,
                isLegacyDonation,
                isShortRow: isLegacyDonation && !isPortrait()
            };
        },
        {
            onBackToAmount,
            submitDonationCompleted,
            selectPaymentProvider,
            setBraintreeDeviceInfo,
            toggleCreditCardForm,
            onOrgHunterCcValidityChange,
            widgetBounce
        }
    ),
    reduxForm({
        destroyOnUnmount: false,
        form: 'donationForm',
        validate
    })
)(DonationForm);
